/**
 * AccountDeleteWebPage — the auth-less /account/delete page.
 *
 * Three phases inside one route:
 *   1. email   — enter the grown-up's email; we POST it to
 *                /family/erasure/email-challenge which always returns 202.
 *                We never reveal whether the email maps to an account.
 *   2. sent    — "check your email". Show the same confirmation regardless,
 *                so the page can't be used to probe who has an account.
 *   3. confirm — reached via the emailed link (/account/delete/confirm?token=…).
 *                Shows the full category summary; on submit it consumes the
 *                token, starts the 30-day window, and switches to the
 *                "scheduled" state. Identical to the in-app flow from there.
 *
 * App stores sometimes require this; it's lower priority than the in-app
 * flow but must work cold from a store listing.
 */

import { useEffect, useState } from "react";
import { Icon } from "../chrome/Icon";
import { ChromeTokens } from "../chrome/tokens";
import { PageShell } from "./PageShell";
import { InvalidTokenError, emailChallenge } from "./api";
import {
  type FamilySummary,
  erasureCategories,
} from "./erasureCategories";
import { GRACE_DAYS, useErasure } from "./useErasure";

const tokens = ChromeTokens;

const EMPTY_FAMILY: FamilySummary = { plan: null, kidNames: [] };

interface Props {
  /** Initial token, e.g. taken from /account/delete/confirm?token=… */
  initialToken?: string | null;
}

type Phase = "email" | "sent" | "confirm" | "scheduled";

export function AccountDeleteWebPage({ initialToken }: Props): JSX.Element {
  const e = useErasure();
  const [phase, setPhase] = useState<Phase>(initialToken ? "confirm" : "email");
  const [email, setEmail] = useState("");
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [token] = useState<string | null>(initialToken ?? null);

  // Once an email-confirm succeeds, the local store flips to grace_pending.
  // Switch to the scheduled phase so we render the date instead of the form.
  useEffect(() => {
    if (phase === "confirm" && e.pending) setPhase("scheduled");
  }, [phase, e.pending]);

  if (phase === "scheduled") {
    return (
      <PageShell maxWidth={500}>
        <ScheduledPanel scheduledStr={e.scheduledStr} />
      </PageShell>
    );
  }

  if (phase === "confirm" && token) {
    return (
      <PageShell maxWidth={520}>
        <ConfirmPanel
          token={token}
          err={err}
          pending={pending}
          onSubmit={async () => {
            setPending(true);
            setErr(null);
            try {
              await e.confirmEmailToken(token);
            } catch (caught) {
              setErr(
                caught instanceof InvalidTokenError
                  ? "This link expired or was already used. Start again from /account/delete."
                  : "Something went wrong. Please try again.",
              );
            } finally {
              setPending(false);
            }
          }}
        />
      </PageShell>
    );
  }

  if (phase === "sent") {
    return (
      <PageShell maxWidth={500}>
        <SentPanel
          email={email}
          onTryAgain={() => {
            setEmail("");
            setPhase("email");
          }}
        />
      </PageShell>
    );
  }

  // phase === "email"
  return (
    <PageShell maxWidth={500}>
      <EmailPanel
        email={email}
        setEmail={setEmail}
        pending={pending}
        err={err}
        onSubmit={async () => {
          setPending(true);
          setErr(null);
          try {
            await emailChallenge(email.trim());
            setPhase("sent");
          } catch {
            setErr("Couldn't send the email. Please try again.");
          } finally {
            setPending(false);
          }
        }}
      />
    </PageShell>
  );
}

function EmailPanel(props: {
  email: string;
  setEmail: (v: string) => void;
  pending: boolean;
  err: string | null;
  onSubmit: () => void;
}): JSX.Element {
  const { email, setEmail, pending, err, onSubmit } = props;
  const valid = /\S+@\S+\.\S+/.test(email);
  return (
    <div>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          fontSize: 11.5,
          fontWeight: 800,
          letterSpacing: ".06em",
          textTransform: "uppercase",
          color: tokens.color.danger,
        }}
      >
        <Icon name="trash-2" size={14} stroke={tokens.color.danger} /> Delete account
      </span>
      <h1
        style={{
          fontFamily: tokens.font.display,
          fontSize: 30,
          margin: `8px 0 10px`,
          lineHeight: 1.1,
          color: tokens.color.ink,
        }}
      >
        Delete your family account
      </h1>
      <p
        style={{
          fontSize: 14.5,
          color: tokens.color.inkMuted,
          lineHeight: 1.6,
          margin: "0 0 20px",
        }}
      >
        Can't get into the app? You can start here. Enter the grown-up's email
        on the account and we'll send a confirmation link — no password needed
        on this page.
      </p>

      <label
        style={{
          display: "flex",
          flexDirection: "column",
          gap: tokens.spacing.xs,
          fontSize: 13,
          fontWeight: 700,
          color: tokens.color.ink,
        }}
      >
        Account email
        <input
          type="email"
          autoComplete="email"
          placeholder="grownup@example.com"
          value={email}
          onChange={(ev) => setEmail(ev.target.value)}
          onKeyDown={(ev) => { if (ev.key === "Enter" && valid && !pending) onSubmit(); }}
          disabled={pending}
          style={{
            padding: `10px ${tokens.spacing.md}px`,
            fontSize: 14,
            fontFamily: tokens.font.ui,
            color: tokens.color.ink,
            background: tokens.color.base,
            border: `1.5px solid ${tokens.color.baseMuted}`,
            borderRadius: tokens.radius.sm,
            outline: "none",
          }}
        />
      </label>

      <button
        type="button"
        disabled={!valid || pending}
        onClick={onSubmit}
        style={{
          width: "100%",
          marginTop: tokens.spacing.lg,
          padding: "11px 18px",
          background: tokens.color.danger,
          color: "#fdf6ec",
          border: "none",
          borderRadius: tokens.radius.pill,
          fontFamily: tokens.font.ui,
          fontWeight: 800,
          fontSize: 14.5,
          cursor: !valid || pending ? "not-allowed" : "pointer",
          opacity: !valid || pending ? 0.6 : 1,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: tokens.spacing.sm,
          boxShadow: tokens.shadow.soft,
        }}
      >
        <Icon name="mail" size={16} stroke="#fdf6ec" />
        {pending ? "Sending…" : "Email me a deletion link"}
      </button>

      <div
        style={{
          marginTop: tokens.spacing.lg,
          padding: `12px ${tokens.spacing.lg}px`,
          borderRadius: tokens.radius.md,
          background: `color-mix(in srgb, ${tokens.color.sky} 20%, ${tokens.color.base})`,
          display: "flex",
          gap: tokens.spacing.md,
          alignItems: "flex-start",
        }}
      >
        <Icon name="shield" size={16} stroke={tokens.color.clay} style={{ flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: 12.5, color: tokens.color.ink, lineHeight: 1.5 }}>
          Nothing is deleted from this page. The emailed link is what confirms
          it's you and starts the 30-day window — you can still cancel during
          that time.
        </div>
      </div>

      {err && (
        <p style={{ marginTop: tokens.spacing.md, fontSize: 13, color: tokens.color.danger }}>{err}</p>
      )}
    </div>
  );
}

function SentPanel({ email, onTryAgain }: { email: string; onTryAgain: () => void }): JSX.Element {
  return (
    <div style={{ textAlign: "center" }}>
      <span
        style={{
          width: 56,
          height: 56,
          borderRadius: 999,
          display: "inline-grid",
          placeItems: "center",
          background: `color-mix(in srgb, ${tokens.color.grass} 26%, ${tokens.color.base})`,
          marginBottom: tokens.spacing.md,
        }}
      >
        <Icon name="mail" size={26} stroke="#3a6e3f" />
      </span>
      <h1
        style={{
          fontFamily: tokens.font.display,
          fontSize: 30,
          margin: `0 0 ${tokens.spacing.md}px`,
          lineHeight: 1.1,
          color: tokens.color.ink,
        }}
      >
        Check your email
      </h1>
      <p
        style={{
          fontSize: 14.5,
          color: tokens.color.inkMuted,
          lineHeight: 1.6,
          margin: "0 auto",
          maxWidth: 380,
        }}
      >
        If <strong style={{ color: tokens.color.ink }}>{email}</strong> has a
        world by me account, we've sent a link to start deleting it. The link
        confirms it's really you — then you'll see exactly what gets removed
        and the 30-day window begins.
      </p>
      <p
        style={{
          fontSize: 12.5,
          color: tokens.color.inkMuted,
          lineHeight: 1.55,
          margin: `${tokens.spacing.lg}px auto 0`,
          maxWidth: 380,
        }}
      >
        Didn't get it? Check spam, or{" "}
        <button
          type="button"
          onClick={onTryAgain}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            color: tokens.color.clay,
            fontWeight: 700,
            fontSize: 12.5,
            textDecoration: "underline",
            cursor: "pointer",
          }}
        >
          try a different email
        </button>.
      </p>
    </div>
  );
}

function ConfirmPanel(props: {
  token: string;
  err: string | null;
  pending: boolean;
  onSubmit: () => void;
}): JSX.Element {
  const { err, pending, onSubmit } = props;
  const cats = erasureCategories(EMPTY_FAMILY);
  const dangerSoft = `color-mix(in srgb, ${tokens.color.danger} 12%, ${tokens.color.base})`;
  const dangerLine = `color-mix(in srgb, ${tokens.color.danger} 30%, ${tokens.color.baseMuted})`;
  return (
    <div>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          fontSize: 11.5,
          fontWeight: 800,
          letterSpacing: ".06em",
          textTransform: "uppercase",
          color: tokens.color.danger,
        }}
      >
        <Icon name="trash-2" size={14} stroke={tokens.color.danger} /> Confirm deletion
      </span>
      <h1
        style={{
          fontFamily: tokens.font.display,
          fontSize: 30,
          margin: `8px 0 10px`,
          lineHeight: 1.1,
          color: tokens.color.ink,
        }}
      >
        Delete your family account
      </h1>
      <p
        style={{
          fontSize: 14.5,
          color: tokens.color.inkMuted,
          lineHeight: 1.6,
          margin: "0 0 20px",
        }}
      >
        Tap below to start the {GRACE_DAYS}-day window. Until then, nothing is
        erased and you can cancel any time.
      </p>

      <ul
        style={{
          listStyle: "none",
          margin: 0,
          padding: `2px ${tokens.spacing.lg}px`,
          border: `1.5px solid ${dangerLine}`,
          borderRadius: tokens.radius.md,
          background: dangerSoft,
        }}
      >
        {cats.map((c, i) => (
          <li
            key={c.label}
            style={{
              display: "flex",
              gap: tokens.spacing.md,
              alignItems: "flex-start",
              padding: "11px 0",
              borderBottom: i === cats.length - 1 ? "none" : `1px solid ${tokens.color.baseMuted}`,
            }}
          >
            <span
              style={{
                width: 30,
                height: 30,
                borderRadius: 9,
                flexShrink: 0,
                display: "grid",
                placeItems: "center",
                background: `color-mix(in srgb, ${tokens.color.danger} 10%, ${tokens.color.base})`,
              }}
            >
              <Icon name={c.icon} size={16} stroke={tokens.color.danger} />
            </span>
            <span style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.3, color: tokens.color.ink }}>
                {c.label}
              </div>
              <div style={{ fontSize: 12.5, color: tokens.color.inkMuted, lineHeight: 1.4, marginTop: 1 }}>
                {c.detail}
              </div>
            </span>
          </li>
        ))}
      </ul>

      <button
        type="button"
        disabled={pending}
        onClick={onSubmit}
        style={{
          width: "100%",
          marginTop: tokens.spacing.lg,
          padding: "11px 18px",
          background: tokens.color.danger,
          color: "#fdf6ec",
          border: "none",
          borderRadius: tokens.radius.pill,
          fontFamily: tokens.font.ui,
          fontWeight: 800,
          fontSize: 14.5,
          cursor: pending ? "wait" : "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: tokens.spacing.sm,
          boxShadow: tokens.shadow.soft,
        }}
      >
        <Icon name="trash-2" size={16} stroke="#fdf6ec" />
        {pending ? "Scheduling…" : "Schedule deletion"}
      </button>

      {err && (
        <p style={{ marginTop: tokens.spacing.md, fontSize: 13, color: tokens.color.danger }}>{err}</p>
      )}
    </div>
  );
}

function ScheduledPanel({ scheduledStr }: { scheduledStr: string | null }): JSX.Element {
  return (
    <div style={{ display: "flex", flexDirection: "column", textAlign: "center" }}>
      <span
        style={{
          width: 56,
          height: 56,
          borderRadius: 999,
          display: "inline-grid",
          placeItems: "center",
          background: `color-mix(in srgb, ${tokens.color.grass} 26%, ${tokens.color.base})`,
          marginBottom: tokens.spacing.md,
          margin: `0 auto ${tokens.spacing.md}px`,
        }}
      >
        <Icon name="calendar-clock" size={26} stroke="#3a6e3f" />
      </span>
      <h1
        style={{
          fontFamily: tokens.font.display,
          fontSize: 28,
          margin: `0 0 ${tokens.spacing.md}px`,
          lineHeight: 1.1,
          color: tokens.color.ink,
        }}
      >
        You have until {scheduledStr ?? "the scheduled date"}
      </h1>
      <p
        style={{
          fontSize: 14.5,
          color: tokens.color.inkMuted,
          lineHeight: 1.6,
          margin: "0 auto",
          maxWidth: 380,
        }}
      >
        Nothing is gone yet. Your family's worlds stay exactly as they are for
        the next <strong>{GRACE_DAYS} days</strong>. On that day everything is
        permanently erased.
      </p>
      <p style={{ fontSize: 12.5, color: tokens.color.inkMuted, marginTop: tokens.spacing.lg }}>
        Sign back in to the app any time before then to cancel.
      </p>
    </div>
  );
}
